import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, EmptyState, Spinner, MEyebrow } from "@cntrl/ui";
import { RequireBand } from "../../app/RequireBand.js";
import { useBandContext } from "../../app/bandContext.js";
import { useActiveBand } from "../../app/activeBand.js";
import { useMe } from "../../app/hooks/useSession.js";
import { switchBand } from "../../app/switchBand.js";
import { usePublicScan, useSetCaseState } from "../../app/hooks/useRoadCases.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { CASE_STATES, CASE_STATE_LABEL, asCaseState } from "../roadCases/caseStates.js";
import styles from "../roadCases/roadCases.module.scss";

/**
 * `/scan?token=` — where a sticker tapped OUTSIDE scan mode lands. iOS reads a paired sticker's URL in the
 * background, AASA routes it into the app, and the deep-link router navigates here. The case is resolved through the
 * PUBLIC resolve (display + the sticker's own band), then three buttons SET its state.
 *
 * It does NOT change the case on arrival (ART-253). With no scan mode running there is no target, and cycling on
 * arrival is exactly the "re-scan flips the case" behaviour the legacy rewrite retired — so the person says where
 * the case is. Scan mode on the case checklist is the tap-and-go path.
 *
 * A sticker of ANOTHER band the person also belongs to switches to that band first (the authed write runs under the
 * current band, so acting from the wrong one would 404). A band they do not belong to says so.
 */
export function ScanScreen({ token }: { token?: string }) {
  return (
    <RequireBand>
      <ScanInner token={token} />
    </RequireBand>
  );
}

function ScanInner({ token }: { token?: string }) {
  const resolve = usePublicScan(token);
  const setState = useSetCaseState();
  const { bandId } = useBandContext();
  const me = useMe();
  const queryClient = useQueryClient();
  const setActiveBand = useActiveBand((s) => s.setActiveBand);
  const [switching, setSwitching] = useState(false);

  if (!token) {
    return (
      <div className={styles.scanState}>
        <EmptyState title="No tag detected" description="This link is missing its sticker code." />
      </div>
    );
  }
  if (resolve.isLoading || switching) {
    return (
      <div className={styles.scanState} role="status" aria-live="polite">
        <Spinner size={26} label="Reading sticker" />
      </div>
    );
  }
  if (resolve.isError || !resolve.data) {
    const notFound = resolve.error instanceof ApiError && resolve.error.status === 404;
    return (
      <div className={styles.scanState}>
        <EmptyState
          title={notFound ? "Sticker not paired" : "Couldn't read sticker"}
          description={
            notFound
              ? "This NFC tag isn't linked to a road case."
              : resolve.error instanceof ApiError
                ? resolve.error.message
                : "Check your connection and try again."
          }
          action={<Button onClick={() => void resolve.refetch()}>Try again</Button>}
        />
      </div>
    );
  }

  const c = resolve.data;

  // The sticker belongs to another band: switch to it if this person has a seat there.
  if (c.bandId && c.bandId !== bandId) {
    const seat = (me.data?.seats ?? []).find((s) => s.kind === "band" && s.bandId === c.bandId);
    if (!seat) {
      return (
        <div className={styles.scanState}>
          <EmptyState
            title="Not your band's case"
            description={`${c.caseName ?? "This case"} belongs to a band you're not a member of.`}
          />
        </div>
      );
    }
    return (
      <div className={styles.scanState}>
        <MEyebrow>Road case</MEyebrow>
        <span className={styles.scanCaseName}>{c.caseName}</span>
        <p className={styles.scanNote}>This case belongs to {seat.displayName ?? "another of your bands"}.</p>
        <Button
          onClick={async () => {
            setSwitching(true);
            await switchBand(c.bandId!, {
              queryClient,
              realtime: null,
              navigate: (id) => setActiveBand(id),
            });
            setSwitching(false);
          }}
        >
          Switch to {seat.displayName ?? "that band"}
        </Button>
      </div>
    );
  }

  const current = asCaseState(c.state);
  return (
    <div className={styles.scanState}>
      <MEyebrow>Road case</MEyebrow>
      <span className={styles.scanCaseName}>{c.caseName}</span>
      <p className={styles.scanNote}>Where is this case?</p>
      <div className={styles.scanStates} role="group" aria-label="Case state">
        {CASE_STATES.map((s) => (
          <button
            key={s}
            type="button"
            className={styles.pill}
            data-state={s}
            aria-pressed={s === current}
            disabled={setState.isPending}
            onClick={() =>
              s !== current &&
              c.caseId &&
              setState.mutate({ caseId: c.caseId, state: s }, { onSuccess: () => void resolve.refetch() })
            }
          >
            {CASE_STATE_LABEL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
